import { apis } from '@/libs-service';
import {
  JOB_DETAIL_ENDPOINT,
  HOT_JOB_KEYWORDS_ENDPOINT,
  JOB_REQUEST_TIMEOUT_MS,
  JOB_SEARCH_SUGGESTIONS_ENDPOINT,
  JOB_SALARY_MARKET_ENDPOINT,
  JOB_SEARCH_ENDPOINT,
  JOBS_PAGE_SIZE,
  SIMILAR_JOBS_ENDPOINT,
  SIMILAR_JOBS_LIMIT,
} from './constants';
import type {
  JobDetailResponse,
  HotJobKeywordsResponse,
  JobSalaryMarketEvaluation,
  JobSalaryMarketEvaluationMap,
  JobSalaryMarketRequest,
  JobSalaryMarketResponse,
  JobSearchFilters,
  JobSearchRequest,
  JobSearchResponse,
  JobSearchSuggestionsResponse,
  SimilarJobsResponse,
} from './model';

export function getHotJobKeywords(
  signal?: AbortSignal
): Promise<HotJobKeywordsResponse> {
  return apis.jobSeeker.get<HotJobKeywordsResponse>(
    HOT_JOB_KEYWORDS_ENDPOINT,
    undefined,
    {
      signal,
      timeoutMs: JOB_REQUEST_TIMEOUT_MS,
    }
  );
}

export function getJobSearchSuggestions(
  query: string,
  signal?: AbortSignal
): Promise<JobSearchSuggestionsResponse> {
  return apis.aiService.get<JobSearchSuggestionsResponse>(
    JOB_SEARCH_SUGGESTIONS_ENDPOINT,
    { q: query },
    {
      signal,
      skipAuth: true,
      timeoutMs: JOB_REQUEST_TIMEOUT_MS,
    }
  );
}

function hasJobSearchFilters(
  filter: JobSearchRequest['filter']
): filter is JobSearchFilters {
  return Boolean(
    filter &&
    Object.values(filter).some(options =>
      options.some(option => option.isSelected)
    )
  );
}

export async function searchJobs(
  request: JobSearchRequest = {},
  signal?: AbortSignal
): Promise<JobSearchResponse> {
  const { filter, ...requestWithoutFilter } = request;
  const payload: JobSearchRequest = {
    limit: JOBS_PAGE_SIZE,
    ...requestWithoutFilter,
    ...(hasJobSearchFilters(filter) ? { filter } : {}),
  };

  return apis.jobSeeker.post<JobSearchResponse, JobSearchRequest>(
    JOB_SEARCH_ENDPOINT,
    payload,
    { signal, skipAuth: true, timeoutMs: JOB_REQUEST_TIMEOUT_MS }
  );
}

export async function getJobDetail(jobId: string): Promise<JobDetailResponse> {
  return apis.jobSeeker.get<JobDetailResponse>(
    `${JOB_DETAIL_ENDPOINT}/${encodeURIComponent(jobId)}`,
    undefined,
    // Public job content must not depend on a possibly revoked session.
    { skipAuth: true, timeoutMs: JOB_REQUEST_TIMEOUT_MS }
  );
}

export async function getSimilarJobs(
  jobId: string,
  limit = SIMILAR_JOBS_LIMIT
): Promise<SimilarJobsResponse> {
  const normalizedLimit = Math.min(Math.max(limit, 1), SIMILAR_JOBS_LIMIT);

  return apis.jobSeeker.get<SimilarJobsResponse>(
    SIMILAR_JOBS_ENDPOINT,
    {
      jobId,
      limit: normalizedLimit,
    },
    { skipAuth: true, timeoutMs: JOB_REQUEST_TIMEOUT_MS }
  );
}

function isJobSalaryMarketEvaluation(
  value: unknown
): value is JobSalaryMarketEvaluation {
  return (
    value === 'nosalary' ||
    value === 'high' ||
    value === 'medium' ||
    value === 'low'
  );
}

function isJobSalaryMarketResult(value: unknown): value is {
  jobId?: string;
  id?: string;
  evaluation: JobSalaryMarketEvaluation;
} {
  return (
    typeof value === 'object' &&
    value !== null &&
    'evaluation' in value &&
    isJobSalaryMarketEvaluation(value.evaluation)
  );
}

function normalizeJobSalaryMarketEvaluations(
  response: JobSalaryMarketResponse,
  requestedJobIds: string[]
): JobSalaryMarketEvaluationMap {
  const requestedJobIdSet = new Set(requestedJobIds);
  const evaluations: JobSalaryMarketEvaluationMap = {};
  const setEvaluation = (jobId: string | undefined, value: unknown) => {
    if (
      jobId &&
      requestedJobIdSet.has(jobId) &&
      isJobSalaryMarketEvaluation(value)
    ) {
      evaluations[jobId] = value;
    }
  };
  const { data } = response;

  if (Array.isArray(data)) {
    data.forEach((item, index) => {
      setEvaluation(
        item.jobId ?? item.id ?? requestedJobIds[index],
        item.evaluation
      );
    });

    return evaluations;
  }

  if (isJobSalaryMarketResult(data)) {
    setEvaluation(data.jobId ?? data.id ?? requestedJobIds[0], data.evaluation);

    return evaluations;
  }

  Object.entries(data).forEach(([jobId, item]) => {
    if (typeof item === 'string') {
      setEvaluation(jobId, item);

      return;
    }

    setEvaluation(item.jobId ?? item.id ?? jobId, item.evaluation);
  });

  return evaluations;
}

export async function getJobSalaryMarketEvaluations(
  jobIds: string[],
  signal?: AbortSignal
): Promise<JobSalaryMarketEvaluationMap> {
  const normalizedJobIds = [...new Set(jobIds.filter(Boolean))];

  if (normalizedJobIds.length === 0) return {};

  const response = await apis.jobSeeker.post<
    JobSalaryMarketResponse,
    JobSalaryMarketRequest
  >(
    JOB_SALARY_MARKET_ENDPOINT,
    { jobIds: normalizedJobIds },
    {
      signal,
      timeoutMs: JOB_REQUEST_TIMEOUT_MS,
    }
  );

  return normalizeJobSalaryMarketEvaluations(response, normalizedJobIds);
}

export function applyJobSalaryMarketEvaluations<
  T extends {
    id: string;
    salaryMarketEvaluation?: JobSalaryMarketEvaluation;
  },
>(jobs: T[], evaluations: JobSalaryMarketEvaluationMap): T[] {
  return jobs.map(job => ({
    ...job,
    salaryMarketEvaluation: evaluations[job.id],
  }));
}

export function getJobSalaryMarketText(
  evaluation?: JobSalaryMarketEvaluation
): string | null {
  if (evaluation === 'high') return 'Lương ngon quá trời 🤩';
  if (evaluation === 'medium') return 'Chuẩn giá thị trường 👌';

  // if (evaluation === 'nosalary') return '(Lương không công khai 😢)';
  return null;
}

export * from './constants';
export * from './share';
export type * from './model';
